/**
 * @module modules/niceDate
 * @description Date formatting utilities using the spacetime library.
 * Provides functions to format dates in human-readable "short" and "long" formats
 * with automatic timezone handling and DST awareness.
 */

import spacetime from 'spacetime';
import informal from 'spacetime-informal';

/**
 * @typedef {Object} NiceDate
 * @property {string} short - Date formatted in M/d/yy format
 * @property {string} long - Date formatted in MMM dd, YYYY h:mm a format with timezone abbreviation
 */

/**
 * @function getNiceDate
 * @description Transform a date into human-readable "short" and "long" formats using spacetime.
 * Automatically detects user's timezone and handles DST. Short format: M/d/yy, Long format: MMM dd, YYYY h:mm a TZ.
 * @param {Date|string|number} date - Date to format (Date object, ISO string, or timestamp)
 * @returns {NiceDate} Object with formatted dates in short and long formats
 */
export function getNiceDate(date) {
  const { tz } = spacetime();
  const s = spacetime(date).goto(tz);
  const display = informal.display(tz);

  // let s = spacetime.now(display.iana)
  let abbrev = '';
  try {
    abbrev = s.isDST() ? display.daylight.abbrev : display.standard.abbrev;
  } catch (e) {
    console.error('could not get abbrev');
  }

  return {
    short: s.unixFmt('M/d/yy'),
    long: `${s.format('{month-short} {date-pad}, {year} {time}')} ${abbrev}`,
  };
}
